<!--
/*
 * Компонент SlideDownloadInterface — пользовательский интерфейс скачивания инструкций
 */
-->
<script lang="ts" setup>
import { onMounted, ref } from 'vue'
import { usePanZoom } from '@/composables/usePanZoom'
import { useDisplay } from 'vuetify'
import { useAdminStore } from '@/modules/admin/store/adminStore'
import { storeToRefs } from 'pinia'
import type { Guide } from '@vpn-tg/vue-client-grpc/proto/guide/guide-endpoint'
import { formatGuideText } from '@/utils/formatGuideText'
import { useI18n } from 'vue-i18n'

const adminStore = useAdminStore()
const { guides } = storeToRefs(adminStore)
const { smAndDown } = useDisplay()
const { t } = useI18n()

const isLoadingGuides = ref(false)

const isViewDialogOpen = ref(false)
const viewingImageUrl = ref('')
const viewingImageName = ref('')
const viewingGuideText = ref('')

const {
  zoomFactor,
  translateX,
  translateY,
  isTransitioning,
  viewerContainerRef,
  zoomIn,
  zoomOut,
  zoomReset
} = usePanZoom(isViewDialogOpen)

const showGuide = async (guide: Guide) => {
  const data = await adminStore.getGuide(guide.id)
  if (!data) return

  const blob = new Blob([data.pdfFile as BlobPart], { type: 'image/*' })
  viewingImageUrl.value = URL.createObjectURL(blob)
  viewingImageName.value = data.name || guide.name
  viewingGuideText.value = data.text || guide.text || ''
  isViewDialogOpen.value = true
}

const closeViewDialog = () => {
  isViewDialogOpen.value = false
  viewingGuideText.value = ''
  if (viewingImageUrl.value) {
    URL.revokeObjectURL(viewingImageUrl.value)
    viewingImageUrl.value = ''
  }
}

onMounted(async () => {
  isLoadingGuides.value = true
  await adminStore.getGuides()
  isLoadingGuides.value = false
})
</script>

<template>
  <div>
    <div class="d-flex justify-center align-center mt-5 mb-3">
      <span class="text-body-1">{{ t('common.viewInstructions') }}</span>
    </div>
    <div class="d-flex flex-column ga-2">
      <div v-if="isLoadingGuides" class="d-flex justify-center pa-4">
        <v-progress-circular indeterminate color="primary" />
      </div>
      <div v-else-if="guides.length === 0" class="text-center text-text-disabled">
        {{ t('common.guidesNotLoaded') }}
      </div>
      <v-btn
        v-for="guide in guides"
        :key="guide.id"
        variant="outlined"
        color="primary"
        block
        @click="showGuide(guide)"
      >
        {{ guide.name }}
      </v-btn>
    </div>

    <v-dialog
      :model-value="isViewDialogOpen"
      :fullscreen="smAndDown"
      :width="smAndDown ? undefined : 1000"
      @update:model-value="closeViewDialog"
    >
      <v-card class="d-flex flex-column h-100">
        <v-card-title class="d-flex align-center justify-space-between border-b pa-4">
          <div class="text-truncate font-weight-bold" style="max-width: 60%">
            {{ viewingImageName }}
          </div>
          <div class="d-flex align-center ga-1">
            <v-btn
              icon="ri-zoom-in-line"
              variant="text"
              density="comfortable"
              :disabled="zoomFactor >= 20"
              @click="zoomIn"
            />
            <v-btn
              icon="ri-zoom-out-line"
              variant="text"
              density="comfortable"
              :disabled="zoomFactor <= 0.5"
              @click="zoomOut"
            />
            <v-btn
              icon="ri-aspect-ratio-line"
              variant="text"
              density="comfortable"
              :disabled="zoomFactor === 1 && translateX === 0 && translateY === 0"
              @click="zoomReset"
            />
            <v-btn
              icon="ri-close-line"
              variant="text"
              density="comfortable"
              class="ml-2"
              @click="closeViewDialog"
            />
          </div>
        </v-card-title>
        <v-card-text
          class="pa-0 d-flex flex-column"
          style="background-color: #121212; min-height: 400px; height: calc(100vh - 80px); overflow: auto;"
        >
          <div
            v-if="viewingGuideText"
            class="guide-text pa-4"
          >
            <div class="d-flex align-center ga-3">
              <v-icon icon="ri-information-line" color="info" size="24" class="flex-shrink-0" />
              <div
                class="guide-text__body text-body-1"
                v-html="formatGuideText(viewingGuideText)"
              />
            </div>
          </div>
          <div
            ref="viewerContainerRef"
            style="display: flex; justify-content: center; align-items: center; width: 100%; min-height: 50%; flex: 1 1 auto; overflow: hidden; position: relative; touch-action: none; user-select: none;"
          >
            <img
              :src="viewingImageUrl"
              :alt="t('common.instructions')"
              :style="{
                transform: `translate3d(${translateX}px, ${translateY}px, 0) scale(${zoomFactor})`,
                transition: isTransitioning ? 'transform 0.2s ease-out' : 'none',
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                transformOrigin: 'center center',
                pointerEvents: 'none',
                userSelect: 'none'
              }"
            />
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.guide-text {
  position: sticky;
  top: 0;
  z-index: 2;
  background: rgba(33, 150, 243, 0.16);
  border-bottom: 1px solid rgba(33, 150, 243, 0.45);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
  white-space: normal;
  word-break: break-word;
}
.guide-text__body {
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.95);
}
.guide-text :deep(a) {
  color: #90caf9;
  font-weight: 600;
  text-decoration: underline;
}
</style>
